import { useState, useEffect, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { motion, useScroll, useSpring, useTransform } from 'framer-motion'
import { ArrowLeft, Share2, ArrowRight, Calendar, Clock, Building2 } from 'lucide-react'
import { API_BASE, apiAssetUrl } from '../config'
import { SafeImage } from '../components/ui/SafeImage'
import { SITE_URL, DEFAULT_OG_IMAGE } from '../data/seo'
import { toEditorCompatibleHtml } from '../utils/richTextLegacy'

interface CaseStudyItem {
  id: number
  title: string
  slug: string
  summary?: string
  cover?: string
  publishedAt?: string
  clientName?: string
  industry?: string
}

type CaseStudyDetail = {
  title: string
  slug?: string
  summary?: string
  content: string
  cover?: string
  publishedAt?: string
  clientName?: string
  industry?: string
  conversionRate?: string
  averageTicketSize?: string
  totalSiteVisits?: string
  campaignDuration?: string
  activePipelineValue?: string
  servicesDelivered?: string
  seoTitle?: string
  seoDescription?: string
  seoKeywords?: string
  focusKeyword?: string
  secondaryKeywords?: string
  canonicalPath?: string
  ogTitle?: string
  ogDescription?: string
  ogImage?: string
  noindex?: boolean
  nofollow?: boolean
  aiSummary?: string
  authorName?: string
  primaryCity?: string
  targetLocations?: string
  reviewedAt?: string
}

function setMetaName(name: string, content: string) {
  let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null
  if (!el) {
    el = document.createElement('meta')
    el.name = name
    document.head.appendChild(el)
  }
  el.content = content
}

function setMetaProperty(property: string, content: string) {
  let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('property', property)
    document.head.appendChild(el)
  }
  el.content = content
}

export default function CaseStudyDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const [cs, setCs] = useState<CaseStudyDetail | null>(null)
  const [related, setRelated] = useState<CaseStudyItem[]>([])
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  const { scrollYProgress } = useScroll()
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  })
  const { scrollY } = useScroll()
  const y = useTransform(scrollY, [0, 500], [0, 150])

  useEffect(() => {
    if (!slug) {
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setNotFound(false)
    fetch(`${API_BASE}/api/public/case-studies/${slug}`)
      .then((res) => {
        if (res.status === 404) {
          setNotFound(true)
          return null
        }
        return res.json()
      })
      .then((data) => {
        if (cancelled) return
        if (data?.success && data?.caseStudy) {
          setCs(data.caseStudy)
          return fetch(`${API_BASE}/api/public/case-studies`)
        }
        setNotFound(true)
      })
      .then((res) => {
        if (cancelled || !res) return
        return res.json()
      })
      .then((data) => {
        if (cancelled || !data?.success || !Array.isArray(data.caseStudies)) return
        const others = data.caseStudies.filter((i: CaseStudyItem) => i.slug !== slug).slice(0, 3)
        setRelated(others)
      })
      .catch(() => setNotFound(true))
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [slug])

  useEffect(() => {
    if (!cs) return
    const path = cs.canonicalPath || `/case-study/${cs.slug || slug}`
    const canonicalPath = path.startsWith('/') ? path : `/${path}`
    const canonical = `${SITE_URL}${canonicalPath}`
    const pageTitle = (cs.seoTitle || `${cs.title} | Case Studies – DigiSutra`).slice(0, 70)
    const pageDesc = (
      cs.seoDescription ||
      cs.aiSummary ||
      cs.summary ||
      'Case studies from DigiSutra – digital marketing agency in Surat. Real results for B2B, real estate & more.'
    ).slice(0, 160)
    const ogTitle = (cs.ogTitle || pageTitle).slice(0, 60)
    const ogDesc = (cs.ogDescription || pageDesc).slice(0, 160)
    const rawImg = cs.ogImage || cs.cover
    const coverResolved = rawImg
      ? rawImg.startsWith('http')
        ? rawImg
        : apiAssetUrl(rawImg)
      : ''
    const ogImage = coverResolved
      ? coverResolved.startsWith('http')
        ? coverResolved
        : `${SITE_URL}${coverResolved}`
      : DEFAULT_OG_IMAGE
    const keywords = [cs.seoKeywords, cs.focusKeyword, cs.secondaryKeywords, cs.primaryCity, cs.targetLocations]
      .filter(Boolean)
      .join(', ')

    document.title = pageTitle
    setMetaName('description', pageDesc)
    if (keywords) setMetaName('keywords', keywords)
    setMetaName(
      'robots',
      [cs.noindex ? 'noindex' : 'index', cs.nofollow ? 'nofollow' : 'follow', 'max-image-preview:large'].join(', ')
    )
    if (cs.authorName) setMetaName('author', cs.authorName)
    if (cs.aiSummary) setMetaName('abstract', cs.aiSummary.slice(0, 300))

    setMetaProperty('og:title', ogTitle)
    setMetaProperty('og:description', ogDesc)
    setMetaProperty('og:url', canonical)
    setMetaProperty('og:image', ogImage)
    setMetaProperty('og:type', 'article')
    setMetaProperty('twitter:card', 'summary_large_image')
    setMetaProperty('twitter:title', ogTitle)
    setMetaProperty('twitter:description', ogDesc)
    setMetaProperty('twitter:image', ogImage)

    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
    if (!canonicalEl) {
      canonicalEl = document.createElement('link')
      canonicalEl.rel = 'canonical'
      document.head.appendChild(canonicalEl)
    }
    canonicalEl.href = canonical
  }, [cs, slug])

  const handleShare = () => {
    if (typeof navigator !== 'undefined' && navigator.share) {
      navigator.share({ title: cs?.title, url: window.location.href, text: cs?.summary }).catch(() => {})
    } else {
      navigator.clipboard?.writeText(window.location.href)
    }
  }

  const bodyHtml = useMemo(() => {
    const raw = toEditorCompatibleHtml(cs?.content || '')
    if (!raw.trim()) return ''
    return raw
  }, [cs?.content])

  const isHtml = Boolean(bodyHtml.trim().includes('<'))
  const dateStr = cs?.publishedAt
    ? new Date(cs.publishedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
    : ''
  const plainText = bodyHtml ? bodyHtml.replace(/<[^>]+>/g, ' ') : ''
  const wc = plainText.trim() ? plainText.trim().split(/\s+/).length : 0
  const readMinutes = Math.max(1, Math.round(wc / 220))

  if (loading) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="pt-28 sm:pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="container-narrow">
          <div className="h-6 w-56 bg-slate-200 dark:bg-slate-700 rounded animate-pulse mb-6" />
          <div className="h-[280px] sm:h-[360px] bg-slate-200 dark:bg-slate-700 rounded-2xl animate-pulse mb-8" />
          <div className="space-y-3 max-w-2xl">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-4 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
            ))}
          </div>
        </div>
      </motion.div>
    )
  }

  if (notFound || !cs) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="pt-28 sm:pt-32 pb-16 px-4 sm:px-6 lg:px-8 text-center">
        <p className="text-slate-600 dark:text-slate-400">Case study not found.</p>
        <Link to="/case-study" className="inline-flex items-center gap-2 mt-4 text-primary dark:text-accent font-medium hover:underline">
          <ArrowLeft size={18} /> Back to Case Studies
        </Link>
      </motion.div>
    )
  }

  return (
    <>
      <motion.div
        className="fixed top-0 left-0 right-0 h-1.5 bg-primary dark:bg-accent origin-left z-50"
        style={{ scaleX }}
      />
      <motion.article
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="pb-16 sm:pb-24"
      >
        <div className="relative h-[60vh] sm:h-[70vh] min-h-[400px] flex items-end overflow-hidden">
          <motion.div className="absolute inset-0 -z-10" style={{ y }}>
            <SafeImage
              src={cs.cover}
              alt=""
              label={cs.title}
              tone="hero"
              className="h-full w-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-900/90 via-navy-900/50 to-transparent dark:from-navy-900 dark:via-navy-900/60" />
          </motion.div>

          <div className="container-wide px-4 sm:px-6 lg:px-8 pb-12 sm:pb-16 w-full z-10">
            <motion.nav
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs sm:text-sm text-white/70 mb-6 sm:mb-8"
              aria-label="Breadcrumb"
            >
              <Link to="/" className="hover:text-white transition-colors">
                Home
              </Link>
              <span aria-hidden className="opacity-50">
                /
              </span>
              <Link to="/case-study" className="hover:text-white transition-colors">
                Case Studies
              </Link>
              <span aria-hidden className="opacity-50">
                /
              </span>
              <span className="text-white truncate max-w-[200px] sm:max-w-xs" title={cs.title}>
                {cs.title}
              </span>
            </motion.nav>

            <div className="max-w-4xl">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="flex flex-wrap items-center gap-3 sm:gap-4 mb-4 sm:mb-6"
              >
                {cs.industry && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/20 border border-primary/30 text-white text-xs sm:text-sm font-medium backdrop-blur-md">
                    <Building2 size={14} />
                    {cs.industry}
                  </span>
                )}
                {cs.clientName && (
                  <span className="text-accent font-semibold text-sm sm:text-base tracking-wide uppercase">
                    {cs.clientName}
                  </span>
                )}
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-3xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] text-white mb-6"
              >
                {cs.title}
              </motion.h1>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex flex-wrap items-center gap-4 sm:gap-6 text-white/80 text-sm sm:text-base"
              >
                {dateStr && (
                  <div className="flex items-center gap-2">
                    <Calendar size={16} className="text-accent" />
                    <span>{dateStr}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Clock size={16} className="text-accent" />
                  <span>{readMinutes} min read</span>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        <div className="container-wide px-4 sm:px-6 lg:px-8 mt-12 sm:mt-16">
          <div className="grid lg:grid-cols-[1fr_320px] gap-8 lg:gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="card-glass rounded-3xl shadow-card overflow-hidden order-2 lg:order-1"
            >
              <div className="p-6 sm:p-10 lg:p-12">
                {cs.aiSummary && (
                  <div className="mb-8 rounded-2xl border border-primary/15 bg-primary/[0.04] p-4 sm:p-5">
                    <p className="text-[0.7rem] font-bold uppercase tracking-[0.14em] text-primary mb-2">
                      Results snapshot
                    </p>
                    <p className="text-slate-700 dark:text-slate-300 leading-relaxed">{cs.aiSummary}</p>
                  </div>
                )}
                {(
                  [
                    ['Conversion', cs.conversionRate],
                    ['Avg ticket', cs.averageTicketSize],
                    ['Visits', cs.totalSiteVisits],
                    ['Duration', cs.campaignDuration],
                    ['Pipeline', cs.activePipelineValue],
                  ] as const
                ).some(([, v]) => v) && (
                  <div className="mb-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {(
                      [
                        ['Conversion', cs.conversionRate],
                        ['Avg ticket', cs.averageTicketSize],
                        ['Visits', cs.totalSiteVisits],
                        ['Duration', cs.campaignDuration],
                        ['Pipeline', cs.activePipelineValue],
                      ] as const
                    )
                      .filter(([, v]) => v)
                      .map(([label, value]) => (
                        <div
                          key={label}
                          className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-800/50 px-3 py-3"
                        >
                          <p className="text-[0.65rem] font-bold uppercase tracking-wide text-slate-400">{label}</p>
                          <p className="text-sm sm:text-base font-semibold text-slate-900 dark:text-white mt-0.5">
                            {value}
                          </p>
                        </div>
                      ))}
                  </div>
                )}
                {isHtml ? (
                  <div
                    className="case-study-content prose prose-slate dark:prose-invert prose-lg max-w-none
                      prose-p:leading-[1.8] prose-p:my-[1.2em] prose-p:text-[1.05rem] sm:prose-p:text-[1.125rem]
                      prose-p:text-slate-700 dark:prose-p:text-slate-300
                      prose-headings:font-display prose-headings:font-bold prose-headings:tracking-tight
                      prose-headings:text-slate-900 dark:prose-headings:text-white
                      prose-strong:text-slate-900 dark:prose-strong:text-white prose-strong:font-semibold
                      prose-a:text-primary dark:prose-a:text-accent prose-a:font-medium prose-a:no-underline hover:prose-a:underline
                      prose-img:max-w-none prose-img:rounded-xl prose-img:h-auto prose-table:block prose-table:w-full prose-table:overflow-x-auto"
                    dangerouslySetInnerHTML={{ __html: bodyHtml }}
                  />
                ) : (
                  <div className="case-study-content max-w-[min(100%,72ch)] mx-auto whitespace-pre-wrap text-slate-700 dark:text-slate-300 leading-[1.8] text-[1.125rem]">
                    {cs.content || 'No content yet.'}
                  </div>
                )}
                <div className="mt-12 pt-8 border-t border-slate-200/70 dark:border-slate-700 flex flex-wrap items-center gap-4 justify-between">
                  <Link
                    to="/case-study"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 font-semibold hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                  >
                    <ArrowLeft size={18} /> Back to Case Studies
                  </Link>
                  <button
                    type="button"
                    onClick={handleShare}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:border-primary dark:hover:border-accent hover:text-primary dark:hover:text-accent font-semibold transition-colors"
                  >
                    <Share2 size={18} /> Share
                  </button>
                </div>
              </div>
            </motion.div>

            <aside className="lg:sticky lg:top-28 order-1 lg:order-2 space-y-6">
              {cs.summary && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                  className="card-glass rounded-2xl p-6 sm:p-8 shadow-card border-l-4 border-l-accent"
                >
                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-3">
                    Executive Summary
                  </h3>
                  <p className="text-slate-700 dark:text-slate-300 leading-relaxed text-lg font-medium">
                    {cs.summary}
                  </p>
                </motion.div>
              )}

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="card-glass rounded-2xl p-6 sm:p-8 shadow-card bg-gradient-to-br from-primary/5 to-transparent dark:from-primary/10"
              >
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                  Ready for similar results?
                </h3>
                <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-6">
                  Book a strategy call with our team to map out a growth plan tailored for your business.
                </p>
                <Link to="/contact" className="btn-primary w-full group">
                  Book Strategy Call
                  <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            </aside>
          </div>

          {related.length > 0 && (
            <motion.section
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5 }}
              className="mt-20 sm:mt-24"
            >
              <div className="flex items-end justify-between gap-4 mb-8 sm:mb-10">
                <div>
                  <span className="text-accent font-semibold text-sm uppercase tracking-wider">Explore</span>
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white mt-2">
                    More Case Studies
                  </h2>
                </div>
                <Link to="/case-study" className="hidden sm:inline-flex items-center gap-2 text-primary dark:text-accent font-semibold hover:underline">
                  View all <ArrowRight size={16} />
                </Link>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                {related.map((item, i) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <Link
                      to={`/case-study/${item.slug}`}
                      className="group block h-full card-glass p-0 overflow-hidden hover:shadow-card-hover dark:hover:shadow-card-hover transition-all duration-300 flex flex-col"
                    >
                      <div className="relative aspect-video overflow-hidden bg-slate-100 dark:bg-slate-800">
                        <SafeImage
                          src={item.cover}
                          alt=""
                          label={item.title}
                          tone="default"
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="pointer-events-none absolute inset-0 bg-black/10 transition-colors duration-300 group-hover:bg-transparent" />
                      </div>
                      <div className="p-6 flex-1 flex flex-col">
                        {(item.clientName || item.industry) && (
                          <p className="text-xs font-semibold text-accent uppercase tracking-wide mb-3">
                            {[item.clientName, item.industry].filter(Boolean).join(' · ')}
                          </p>
                        )}
                        <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white group-hover:text-primary dark:group-hover:text-accent transition-colors line-clamp-2 mb-3">
                          {item.title}
                        </h3>
                        {item.summary && (
                          <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-3 flex-1">
                            {item.summary}
                          </p>
                        )}
                        <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary dark:text-accent">
                          Read case study <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                        </span>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
              <div className="mt-8 text-center sm:hidden">
                <Link to="/case-study" className="inline-flex items-center gap-2 text-primary dark:text-accent font-semibold hover:underline">
                  View all case studies <ArrowRight size={16} />
                </Link>
              </div>
            </motion.section>
          )}
        </div>
      </motion.article>
    </>
  )
}
